'use client';

import Link from 'next/link';
import { useState, useEffect, Suspense, type CSSProperties, type ReactNode } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/supabase/client';
import { PLAN_PLUS } from '@/config/pricing';
import { isPlusUser } from '@/lib/entitlements';

const NAVY = '#0f172a';
const BLUE = '#0284c7';
const BLUE_BRIGHT = '#0ea5e9';
const INK = '#0f172a';
const MUTED = '#475569';
const BORDER = '#e2e8f0';
const SURFACE = '#ffffff';
const SURFACE_MUTED = '#f8fafc';
const SURFACE_ALT = '#f1f5f9';
const SUCCESS = '#047857';

const sectionInner: CSSProperties = {
  maxWidth: 1120,
  margin: '0 auto',
  padding: 'clamp(56px, 7vw, 88px) 20px',
};

const eyebrowStyle: CSSProperties = {
  margin: '0 0 12px',
  color: BLUE,
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
};

const sectionTitleStyle: CSSProperties = {
  margin: '0 0 16px',
  color: INK,
  fontSize: 'clamp(1.8rem, 4vw, 2.6rem)',
  fontWeight: 800,
  lineHeight: 1.15,
  letterSpacing: '-0.03em',
};

const bodyStyle: CSSProperties = {
  margin: 0,
  color: MUTED,
  fontSize: '1.05rem',
  lineHeight: 1.7,
};

const primaryButton: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 50,
  padding: '14px 26px',
  borderRadius: 8,
  background: BLUE_BRIGHT,
  color: SURFACE,
  fontSize: 16,
  fontWeight: 800,
  textDecoration: 'none',
  border: 'none',
  cursor: 'pointer',
  boxShadow: '0 4px 14px rgba(14, 165, 233, 0.32)',
  fontFamily: 'inherit',
};

const secondaryButton: CSSProperties = {
  ...primaryButton,
  background: 'transparent',
  border: '1.5px solid rgba(255,255,255,0.75)',
  boxShadow: 'none',
};

const lightSecondaryButton: CSSProperties = {
  ...primaryButton,
  background: SURFACE,
  color: BLUE,
  border: `1.5px solid ${BLUE}`,
  boxShadow: 'none',
};

const cardStyle: CSSProperties = {
  height: '100%',
  padding: 28,
  border: `1px solid ${BORDER}`,
  borderRadius: 14,
  background: SURFACE,
  boxShadow: '0 6px 22px rgba(15, 23, 42, 0.06)',
};

const inlineLinkStyle: CSSProperties = {
  color: BLUE,
  fontWeight: 700,
  textDecoration: 'none',
};

// Verified Free entitlements (code-backed)
const FREE_FEATURES = [
  'Create an account and browse available listings',
  'Basic search and filters',
  'View photos, address, location, and map details',
  'Wholesalers: post up to 5 active listings',
] as const;

// Verified Plus entitlements (code-backed via isPlusUser / permissions)
const PLUS_FEATURES = [
  'Direct messaging and seller contact tools',
  'Saved searches and alerts',
  'Watchlists and favorites',
  'Heatmap insights',
  'Unlimited active listings for wholesalers',
  'Ad-free browsing (no sponsored tiles)',
] as const;

const faqs: { q: string; a: ReactNode }[] = [
  {
    q: 'Can I create an account for free?',
    a: 'Yes. You can create a Free account and start browsing or posting within Free plan limits without paying first.',
  },
  {
    q: 'What can I do with the Free plan?',
    a: 'Free accounts can browse listings, use basic search and filters, and view photos and map details. Wholesalers can also post up to 5 active listings. Messaging, contact tools, watchlists, saved-search alerts, and heatmaps require Plus.',
  },
  {
    q: 'When should I upgrade to Plus?',
    a: 'Upgrade when you are ready to act: contact sellers or buyers, save searches and receive alerts, organize deals with watchlists, use heatmap insights, or post more than 5 active listings as a wholesaler.',
  },
  {
    q: 'Can wholesalers post for free?',
    a: 'Yes. Wholesalers can post up to 5 active listings on a Free account. Plus removes that active-listing limit.',
  },
  {
    q: 'Does Plus include messaging or contact access?',
    a: 'Yes. Messaging and seller contact tools require Plus for all users.',
  },
  {
    q: 'Are subscriptions monthly or annual?',
    a: (
      <>
        Both. Plus is ${PLAN_PLUS.monthlyPrice.toFixed(2)}/month or ${PLAN_PLUS.yearlyPrice.toFixed(2)}/year ({PLAN_PLUS.yearlySavingsLabel} vs paying monthly). Your selected plan is charged immediately at checkout.
      </>
    ),
  },
  {
    q: 'Can I cancel?',
    a: (
      <>
        Yes. Paid subscriptions renew automatically until canceled. You can manage or cancel through Billing according to the{' '}
        <Link href="/terms" className="oad-pricing-link" style={inlineLinkStyle}>
          Terms of Service
        </Link>
        .
      </>
    ),
  },
  {
    q: 'Does Plus guarantee access to deals or successful transactions?',
    a: 'No. Off Axis Deals is a marketplace and connection platform. Plus unlocks specific tools; it does not guarantee listings, transactions, financing, closings, profits, or outcomes.',
  },
];

type PlanStatus = 'loading' | 'signed_out' | 'free' | 'plus' | 'error';

function CheckItem({ text, included = true }: { text: string; included?: boolean }) {
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '8px 0',
        fontSize: 15,
        lineHeight: 1.5,
        color: included ? '#334155' : '#94a3b8',
      }}
    >
      <span
        aria-hidden
        style={{
          color: included ? SUCCESS : '#cbd5e1',
          fontWeight: 800,
          fontSize: 16,
          lineHeight: 1.4,
          flexShrink: 0,
        }}
      >
        {included ? '✓' : '–'}
      </span>
      <span>{text}</span>
    </li>
  );
}

function PricingPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { session, loading: authLoading, profile, profileLoading, authBroken } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const isLoggedIn = Boolean(session);
  const paid = isPlusUser(profile);
  const resolvingProfile = isLoggedIn && profileLoading;

  let planStatus: PlanStatus = 'signed_out';
  if (authLoading || resolvingProfile) {
    planStatus = 'loading';
  } else if (authBroken) {
    planStatus = 'error';
  } else if (!isLoggedIn) {
    planStatus = 'signed_out';
  } else if (!profile) {
    // Signed in but profile could not be resolved — do not invent Free/Plus status
    planStatus = 'error';
  } else if (paid) {
    planStatus = 'plus';
  } else {
    planStatus = 'free';
  }

  useEffect(() => {
    const period = searchParams?.get('period');
    if (period === 'monthly' || period === 'yearly') {
      setBillingPeriod(period);
    }
  }, [searchParams]);

  const yearlyFullPrice = Number((PLAN_PLUS.monthlyPrice * 12).toFixed(2));
  const yearlySavings = Number((yearlyFullPrice - PLAN_PLUS.yearlyPrice).toFixed(2));
  const yearlySavingsPercent = Math.round((yearlySavings / yearlyFullPrice) * 100);
  const plusPrice =
    billingPeriod === 'yearly'
      ? `$${PLAN_PLUS.yearlyPrice.toFixed(2)}`
      : `$${PLAN_PLUS.monthlyPrice.toFixed(2)}`;
  const plusPeriod = billingPeriod === 'yearly' ? '/year' : '/month';

  const handleUpgrade = async () => {
    if (planStatus === 'plus') {
      router.push('/billing');
      return;
    }
    if (planStatus === 'loading' || planStatus === 'error') {
      return;
    }

    const {
      data: { session: supabaseSession },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !supabaseSession?.access_token) {
      router.push(`/login?next=${encodeURIComponent('/pricing')}`);
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await fetch('/api/billing/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseSession.access_token}`,
        },
        body: JSON.stringify({ period: billingPeriod }),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        if (response.status === 401) {
          alert('Please sign in to upgrade your plan.');
          router.push(`/login?next=${encodeURIComponent('/pricing')}`);
          return;
        }
        // Server-side duplicate-subscription guard
        if (response.status === 409 || errorData.error === 'already_subscribed') {
          router.push(typeof errorData.manageUrl === 'string' ? errorData.manageUrl : '/billing');
          return;
        }
        alert(`Error: ${errorData.message || errorData.error || 'Something went wrong. Please try again.'}`);
        return;
      }

      const data = await response.json();
      const checkoutUrl = data.checkoutUrl || data.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        alert('Error: No checkout URL received. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Error processing payment. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const freeCtaLabel =
    planStatus === 'free'
      ? 'Current Plan'
      : planStatus === 'plus'
        ? 'Included in Plus'
        : planStatus === 'loading'
          ? 'Checking…'
          : planStatus === 'error'
            ? 'View Account'
            : 'Get 6 Months of Plus Free';

  const plusCtaLabel =
    planStatus === 'plus'
      ? 'Manage Subscription'
      : planStatus === 'free'
        ? `Upgrade to ${PLAN_PLUS.planName}`
        : planStatus === 'loading'
          ? 'Checking…'
          : planStatus === 'error'
            ? 'View Account'
            : `Upgrade to ${PLAN_PLUS.planName}`;

  return (
    <div className="oad-pricing-page">
      <style>{`
        .oad-pricing-page { background: ${SURFACE_MUTED}; color: ${INK}; }
        .oad-pricing-hero {
          position: relative;
          overflow: hidden;
          background:
            radial-gradient(ellipse 80% 60% at 20% 0%, rgba(14,165,233,.18), transparent 55%),
            radial-gradient(ellipse 70% 50% at 90% 20%, rgba(15,23,42,.08), transparent 50%),
            linear-gradient(180deg, #0f172a 0%, #1e293b 55%, #0f172a 100%);
          color: ${SURFACE};
        }
        .oad-pricing-hero-inner {
          position: relative;
          z-index: 1;
          max-width: 1120px;
          margin: 0 auto;
          padding: clamp(64px, 9vw, 100px) 20px clamp(56px, 7vw, 80px);
        }
        .oad-pricing-cta-row { display:flex; flex-wrap:wrap; gap:12px; margin-top:28px; }
        .oad-pricing-plans {
          display:grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 24px;
          max-width: 900px;
          margin: 0 auto;
        }
        .oad-pricing-role-grid {
          display:grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 24px;
        }
        .oad-pricing-faq-grid {
          display:grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
        }
        .oad-pricing-toggle {
          display:inline-flex;
          gap:8px;
          background: ${SURFACE_ALT};
          padding:4px;
          border-radius:10px;
          border: 1px solid ${BORDER};
        }
        .oad-pricing-link:hover { text-decoration: underline !important; }
        .oad-pricing-primary:hover { background: ${BLUE} !important; }
        .oad-pricing-secondary:hover { background: rgba(255,255,255,.10) !important; }
        .oad-pricing-light-secondary:hover { background: #f0f9ff !important; }
        .oad-pricing-plan-btn:hover:not(:disabled) { opacity: 0.92; }
        .oad-pricing-plan-btn:disabled { cursor: default; opacity: 0.85; }
        @media (max-width: 768px) {
          .oad-pricing-plans,
          .oad-pricing-role-grid,
          .oad-pricing-faq-grid { grid-template-columns: 1fr; }
          .oad-pricing-cta-row a,
          .oad-pricing-cta-row button { width: 100%; text-align: center; }
        }
      `}</style>

      {/* Hero */}
      <section className="oad-pricing-hero" aria-labelledby="pricing-title">
        <div className="oad-pricing-hero-inner">
          <p style={{ ...eyebrowStyle, color: '#7dd3fc' }}>Pricing</p>
          <h1
            id="pricing-title"
            style={{
              margin: '0 0 18px',
              color: SURFACE,
              fontSize: 'clamp(2.1rem, 5vw, 3.4rem)',
              fontWeight: 850,
              lineHeight: 1.08,
              letterSpacing: '-0.04em',
              maxWidth: 720,
            }}
          >
            Start Free. Upgrade When You&apos;re Ready to Act.
          </h1>
          <p
            style={{
              ...bodyStyle,
              maxWidth: 620,
              color: 'rgba(248,250,252,.9)',
              fontSize: 'clamp(1rem,2.2vw,1.18rem)',
            }}
          >
            Browse or post on Free without committing to a paid plan. Upgrade to {PLAN_PLUS.planName} when
            you need direct contact, alerts, organization tools, or higher listing limits.
          </p>

          {planStatus === 'loading' && (
            <p style={{ marginTop: 20, color: 'rgba(248,250,252,.75)', fontSize: 15 }}>
              Checking your account status…
            </p>
          )}

          {planStatus === 'error' && (
            <p style={{ marginTop: 20, color: '#fca5a5', fontSize: 15 }}>
              We couldn&apos;t load your subscription status.{' '}
              <Link href="/account" style={{ ...inlineLinkStyle, color: '#7dd3fc' }}>
                View Account
              </Link>{' '}
              or refresh and try again.
            </p>
          )}

          {planStatus === 'plus' && (
            <p
              style={{
                marginTop: 20,
                display: 'inline-block',
                padding: '8px 14px',
                borderRadius: 8,
                background: 'rgba(16,185,129,.18)',
                border: '1px solid rgba(52,211,153,.35)',
                color: '#a7f3d0',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              You&apos;re on {PLAN_PLUS.planName}. This is your current plan.
            </p>
          )}

          {planStatus === 'free' && (
            <p
              style={{
                marginTop: 20,
                display: 'inline-block',
                padding: '8px 14px',
                borderRadius: 8,
                background: 'rgba(255,255,255,.08)',
                border: '1px solid rgba(255,255,255,.2)',
                color: 'rgba(248,250,252,.9)',
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              You&apos;re on Free. Upgrade anytime when you&apos;re ready to act.
            </p>
          )}

          <div className="oad-pricing-cta-row">
            {planStatus === 'loading' ? (
              <button
                type="button"
                disabled
                style={{
                  ...primaryButton,
                  opacity: 0.7,
                  cursor: 'default',
                  boxShadow: 'none',
                }}
              >
                Checking account…
              </button>
            ) : planStatus === 'error' ? (
              <Link href="/account" className="oad-pricing-primary" style={primaryButton}>
                View Account
              </Link>
            ) : planStatus === 'signed_out' ? (
              <Link href="/signup" className="oad-pricing-primary" style={primaryButton}>
                Get 6 Months of Plus Free
              </Link>
            ) : planStatus === 'free' ? (
              <button
                type="button"
                className="oad-pricing-primary"
                style={primaryButton}
                onClick={handleUpgrade}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? 'Starting checkout…' : `Upgrade to ${PLAN_PLUS.planName}`}
              </button>
            ) : (
              <Link href="/billing" className="oad-pricing-primary" style={primaryButton}>
                Manage Subscription
              </Link>
            )}
            <Link href="/listings" className="oad-pricing-secondary" style={secondaryButton}>
              Browse Deals
            </Link>
            <Link href="/how-it-works" className="oad-pricing-secondary" style={secondaryButton}>
              How It Works
            </Link>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section style={{ background: SURFACE }} aria-labelledby="plans-title">
        <div style={sectionInner}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <p style={eyebrowStyle}>Plans</p>
            <h2 id="plans-title" style={{ ...sectionTitleStyle, marginBottom: 12 }}>
              Free for discovery. {PLAN_PLUS.planName} for action.
            </h2>
            <p style={{ ...bodyStyle, maxWidth: 560, margin: '0 auto 24px' }}>
              Two clear stages. This is not a checkout wall. Start where you are, and upgrade when tools matter.
            </p>

            <div className="oad-pricing-toggle" role="group" aria-label="Billing period">
              <button
                type="button"
                onClick={() => setBillingPeriod('monthly')}
                style={{
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: 'none',
                  background: billingPeriod === 'monthly' ? SURFACE : 'transparent',
                  color: billingPeriod === 'monthly' ? INK : MUTED,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: 14,
                  boxShadow: billingPeriod === 'monthly' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                }}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod('yearly')}
                style={{
                  padding: '10px 18px',
                  borderRadius: 8,
                  border: 'none',
                  background: billingPeriod === 'yearly' ? SURFACE : 'transparent',
                  color: billingPeriod === 'yearly' ? INK : MUTED,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: 14,
                  boxShadow: billingPeriod === 'yearly' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                }}
              >
                Yearly
                <span style={{ marginLeft: 6, fontSize: 12, color: SUCCESS, fontWeight: 800 }}>
                  ({PLAN_PLUS.yearlySavingsLabel})
                </span>
              </button>
            </div>
            <p style={{ margin: '12px 0 0', fontSize: 13, color: MUTED }}>
              Join by December 31, 2026 and get 6 months of Plus free—no card, no code,
              and no automatic renewal. If you later choose a paid plan, it is charged
              immediately at Stripe checkout.
            </p>
          </div>

          <div className="oad-pricing-plans">
            {/* Free */}
            <article
              style={{
                ...cardStyle,
                position: 'relative',
                border:
                  planStatus === 'free' ? `2px solid ${NAVY}` : `1px solid ${BORDER}`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {planStatus === 'free' && (
                <div
                  style={{
                    position: 'absolute',
                    top: -12,
                    left: 20,
                    background: NAVY,
                    color: SURFACE,
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  Current Plan
                </div>
              )}
              <h3 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800 }}>Free</h3>
              <p style={{ margin: '0 0 16px', color: MUTED, fontSize: 15, lineHeight: 1.55 }}>
                Explore the marketplace, review listings, and decide whether Off Axis Deals fits your
                workflow.
              </p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                <span style={{ fontSize: 40, fontWeight: 850, letterSpacing: '-0.03em' }}>$0</span>
                <span style={{ color: MUTED, fontSize: 16 }}>/month</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 28px', flex: 1 }}>
                {FREE_FEATURES.map((f) => (
                  <CheckItem key={f} text={f} />
                ))}
                <CheckItem text="Messaging, alerts, watchlists, and heatmaps" included={false} />
              </ul>
              {planStatus === 'signed_out' ? (
                <Link
                  href="/signup"
                  className="oad-pricing-light-secondary"
                  style={{ ...lightSecondaryButton, width: '100%' }}
                >
                  {freeCtaLabel}
                </Link>
              ) : planStatus === 'error' ? (
                <Link
                  href="/account"
                  className="oad-pricing-light-secondary"
                  style={{ ...lightSecondaryButton, width: '100%' }}
                >
                  {freeCtaLabel}
                </Link>
              ) : (
                <button
                  type="button"
                  className="oad-pricing-plan-btn"
                  disabled
                  style={{
                    ...lightSecondaryButton,
                    width: '100%',
                    background: SURFACE_ALT,
                    color: MUTED,
                    border: `1px solid ${BORDER}`,
                    cursor: 'default',
                  }}
                >
                  {freeCtaLabel}
                </button>
              )}
            </article>

            {/* Plus */}
            <article
              style={{
                ...cardStyle,
                position: 'relative',
                border:
                  planStatus === 'plus'
                    ? `2px solid ${SUCCESS}`
                    : `2px solid ${BLUE_BRIGHT}`,
                boxShadow:
                  planStatus === 'plus'
                    ? '0 8px 28px rgba(4, 120, 87, 0.12)'
                    : '0 8px 28px rgba(14, 165, 233, 0.14)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -12,
                  left: 20,
                  background: planStatus === 'plus' ? SUCCESS : BLUE_BRIGHT,
                  color: SURFACE,
                  padding: '4px 12px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 800,
                }}
              >
                {planStatus === 'plus' ? 'Current Plan' : 'For serious deal flow'}
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800 }}>
                {PLAN_PLUS.planName}
              </h3>
              <p style={{ margin: '0 0 16px', color: MUTED, fontSize: 15, lineHeight: 1.55 }}>
                Built for users ready to pursue or distribute deals, with contact, alerts, and
                organization tools.
              </p>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                <span style={{ fontSize: 40, fontWeight: 850, letterSpacing: '-0.03em' }}>
                  {plusPrice}
                </span>
                <span style={{ color: MUTED, fontSize: 16 }}>{plusPeriod}</span>
              </div>
              {billingPeriod === 'yearly' ? (
                <p style={{ margin: '0 0 8px', fontSize: 14 }}>
                  <span style={{ color: '#94a3b8', textDecoration: 'line-through', marginRight: 8 }}>
                    ${yearlyFullPrice}/year
                  </span>
                  <span style={{ color: SUCCESS, fontWeight: 700 }}>
                    Save ${yearlySavings}/year ({yearlySavingsPercent}% off)
                  </span>
                </p>
              ) : (
                <p style={{ margin: '0 0 8px', fontSize: 13, color: SUCCESS, fontWeight: 700 }}>
                  Save ${yearlySavings}/year ({yearlySavingsPercent}% off) with yearly billing
                </p>
              )}
              <p style={{ margin: '0 0 8px', fontSize: 13, color: MUTED }}>
                Billed securely via Stripe on the web. Your selected plan is charged immediately.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 28px', flex: 1 }}>
                <CheckItem text="Everything in Free" />
                {PLUS_FEATURES.map((f) => (
                  <CheckItem key={f} text={f} />
                ))}
              </ul>
              {planStatus === 'plus' ? (
                <Link
                  href="/billing"
                  className="oad-pricing-primary"
                  style={{ ...primaryButton, width: '100%', background: SUCCESS, boxShadow: 'none' }}
                >
                  {plusCtaLabel}
                </Link>
              ) : planStatus === 'error' ? (
                <Link
                  href="/account"
                  className="oad-pricing-primary"
                  style={{ ...primaryButton, width: '100%' }}
                >
                  {plusCtaLabel}
                </Link>
              ) : planStatus === 'loading' ? (
                <button
                  type="button"
                  className="oad-pricing-plan-btn"
                  disabled
                  style={{ ...primaryButton, width: '100%', opacity: 0.7, cursor: 'default' }}
                >
                  {plusCtaLabel}
                </button>
              ) : (
                <button
                  type="button"
                  className="oad-pricing-plan-btn oad-pricing-primary"
                  onClick={handleUpgrade}
                  disabled={checkoutLoading}
                  style={{ ...primaryButton, width: '100%' }}
                >
                  {checkoutLoading ? 'Starting checkout…' : plusCtaLabel}
                </button>
              )}
              {planStatus === 'signed_out' && (
                <p style={{ margin: '12px 0 0', fontSize: 13, color: MUTED, textAlign: 'center' }}>
                  You&apos;ll sign in first, then continue to Stripe checkout.
                </p>
              )}
            </article>
          </div>
        </div>
      </section>

      {/* Role value */}
      <section style={{ background: SURFACE_ALT }} aria-labelledby="roles-title">
        <div style={sectionInner}>
          <p style={eyebrowStyle}>By role</p>
          <h2 id="roles-title" style={sectionTitleStyle}>
            What Free and {PLAN_PLUS.planName} mean for your work
          </h2>
          <div className="oad-pricing-role-grid" style={{ marginTop: 28 }}>
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800 }}>Investors</h3>
              <p style={{ ...bodyStyle, fontSize: '0.98rem', marginBottom: 14 }}>
                <strong style={{ color: INK }}>Free:</strong> Browse off-market listings, review
                photos and details, and use basic search to understand available deal flow.
              </p>
              <p style={{ ...bodyStyle, fontSize: '0.98rem', margin: 0 }}>
                <strong style={{ color: INK }}>{PLAN_PLUS.planName}:</strong> Message and contact
                sellers, save watchlists, set saved-search alerts, and use heatmap insights when
                you&apos;re ready to act.
              </p>
            </div>
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800 }}>Wholesalers</h3>
              <p style={{ ...bodyStyle, fontSize: '0.98rem', marginBottom: 14 }}>
                <strong style={{ color: INK }}>Free:</strong> Start posting up to 5 active
                listings and reach investors browsing the marketplace.
              </p>
              <p style={{ ...bodyStyle, fontSize: '0.98rem', margin: 0 }}>
                <strong style={{ color: INK }}>{PLAN_PLUS.planName}:</strong> Unlimited active
                listings, messaging with interested buyers, and organization tools to manage deal
                flow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section style={{ background: SURFACE }} aria-labelledby="trust-title">
        <div style={{ ...sectionInner, maxWidth: 800 }}>
          <p style={eyebrowStyle}>Trust</p>
          <h2 id="trust-title" style={sectionTitleStyle}>
            Clear expectations
          </h2>
          <ul
            style={{
              ...bodyStyle,
              margin: '8px 0 0',
              paddingLeft: 20,
              fontSize: '1rem',
            }}
          >
            <li style={{ marginBottom: 10 }}>You can start free and upgrade only when you need paid tools.</li>
            <li style={{ marginBottom: 10 }}>
              Paid access unlocks specific verified features. It does not guarantee deals or outcomes.
            </li>
            <li style={{ marginBottom: 10 }}>
              Subscriptions renew until canceled according to the{' '}
              <Link href="/terms" className="oad-pricing-link" style={inlineLinkStyle}>
                Terms of Service
              </Link>
              .
            </li>
            <li style={{ marginBottom: 10 }}>
              Off Axis Deals is a marketplace and connection platform. It does not guarantee listings,
              transactions, financing, closings, profits, or success.
            </li>
          </ul>
          <p style={{ ...bodyStyle, marginTop: 20, fontSize: 15 }}>
            See also{' '}
            <Link href="/privacy" className="oad-pricing-link" style={inlineLinkStyle}>
              Privacy Policy
            </Link>
            ,{' '}
            <Link href="/disclaimer" className="oad-pricing-link" style={inlineLinkStyle}>
              Disclaimer
            </Link>
            , and{' '}
            <Link href="/how-it-works" className="oad-pricing-link" style={inlineLinkStyle}>
              How It Works
            </Link>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: SURFACE_MUTED }} aria-labelledby="faq-title">
        <div style={sectionInner}>
          <p style={eyebrowStyle}>FAQ</p>
          <h2 id="faq-title" style={{ ...sectionTitleStyle, marginBottom: 28 }}>
            Pricing questions
          </h2>
          <div className="oad-pricing-faq-grid">
            {faqs.map(({ q, a }) => (
              <div key={q} style={{ ...cardStyle, padding: 22 }}>
                <h3 style={{ margin: '0 0 10px', fontSize: 17, fontWeight: 800, color: INK }}>{q}</h3>
                <p style={{ ...bodyStyle, fontSize: '0.95rem', margin: 0 }}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section
        style={{
          background: `linear-gradient(180deg, ${NAVY} 0%, #1e293b 100%)`,
          color: SURFACE,
        }}
      >
        <div style={{ ...sectionInner, textAlign: 'center', maxWidth: 720 }}>
          <h2
            style={{
              ...sectionTitleStyle,
              color: SURFACE,
              marginBottom: 14,
            }}
          >
            {planStatus === 'plus'
              ? 'Your Plus tools are ready'
              : 'Start free. Upgrade when you need to act.'}
          </h2>
          <p style={{ ...bodyStyle, color: 'rgba(248,250,252,.88)', marginBottom: 28 }}>
            {planStatus === 'plus'
              ? 'Manage billing, review your account, or keep browsing deals.'
              : 'Create a Free account to explore Off Axis Deals, then unlock Plus when contact, alerts, and organization matter.'}
          </p>
          <div className="oad-pricing-cta-row" style={{ justifyContent: 'center' }}>
            {planStatus === 'plus' ? (
              <>
                <Link href="/billing" className="oad-pricing-primary" style={primaryButton}>
                  Manage Subscription
                </Link>
                <Link href="/account" className="oad-pricing-secondary" style={secondaryButton}>
                  View Account
                </Link>
              </>
            ) : planStatus === 'free' ? (
              <>
                <button
                  type="button"
                  className="oad-pricing-primary"
                  style={primaryButton}
                  onClick={handleUpgrade}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? 'Starting checkout…' : `Upgrade to ${PLAN_PLUS.planName}`}
                </button>
                <Link href="/listings" className="oad-pricing-secondary" style={secondaryButton}>
                  Browse Deals
                </Link>
              </>
            ) : planStatus === 'error' ? (
              <>
                <Link href="/account" className="oad-pricing-primary" style={primaryButton}>
                  View Account
                </Link>
                <Link href="/listings" className="oad-pricing-secondary" style={secondaryButton}>
                  Browse Deals
                </Link>
              </>
            ) : planStatus === 'loading' ? (
              <button
                type="button"
                disabled
                style={{ ...primaryButton, opacity: 0.7, cursor: 'default', boxShadow: 'none' }}
              >
                Checking account…
              </button>
            ) : (
              <>
                <Link href="/signup" className="oad-pricing-primary" style={primaryButton}>
                  Get 6 Months of Plus Free
                </Link>
                <Link href="/listings" className="oad-pricing-secondary" style={secondaryButton}>
                  Browse Deals
                </Link>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function PricingPageClient() {
  return (
    <Suspense
      fallback={
        <div style={{ padding: 48, textAlign: 'center', color: MUTED, background: SURFACE_MUTED }}>
          Loading pricing…
        </div>
      }
    >
      <PricingPageInner />
    </Suspense>
  );
}
